import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { api } from '../services/apiClient';
import { theme } from '../styles/theme';
import Button from '../components/ui/Button';
import Card from '../components/ui/Card';
import SectionTitle from '../components/ui/SectionTitle';

// Initialize Stripe
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY || 'pk_test_placeholder');

const PaymentPage = () => {
  return (
    <Elements stripe={stripePromise}>
      <PaymentForm />
    </Elements>
  );
};

const PaymentForm = () => {
  const navigate = useNavigate();
  const stripe = useStripe();
  const elements = useElements();
  const [searchParams] = useSearchParams();
  const orderId = searchParams.get('order_id');
  
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [stockReserved, setStockReserved] = useState(false);

  useEffect(() => {
    if (orderId) {
      fetchOrderDetails();
    } else {
      setError('No order ID provided');
      setLoading(false);
    }
  }, [orderId]);

  const fetchOrderDetails = async () => {
    try {
      setLoading(true);
      const data = await api.payments.getOrderForPayment(orderId);
      setOrder(data);
    } catch (err) {
      console.error('Error fetching order:', err);
      setError('Failed to load order details. The order may be invalid or expired.');
    } finally {
      setLoading(false);
    }
  };

  const handlePayment = async (e) => {
    e.preventDefault();
    
    if (!stripe || !elements) {
      setError('Stripe is not loaded yet. Please try again.');
      return;
    }

    if (!order) {
      setError('Order information not available.');
      return;
    }

    const cardElement = elements.getElement(CardElement);
    if (!cardElement) {
      setError('Card element not found.');
      return;
    }

    setProcessing(true);
    setError('');

    try {
      // Create payment intent - this will atomically reserve stock
      console.log('Creating payment intent and reserving stock for order:', orderId);
      
      const paymentData = await api.payments.createPaymentIntent({
        order_id: orderId,
        amount: order.total,
        currency: order.currency || 'USD',
      });
      
      // Stock is now reserved!
      setStockReserved(true);
      console.log('Stock reserved successfully, payment intent created');

      // Confirm card payment
      const { error: stripeError, paymentIntent } = await stripe.confirmCardPayment(
        paymentData.client_secret,
        {
          payment_method: {
            card: cardElement,
            billing_details: {
              name: order.shipping_address.full_name,
              address: {
                line1: order.shipping_address.address_line1,
                line2: order.shipping_address.address_line2,
                city: order.shipping_address.city,
                state: order.shipping_address.state,
                postal_code: order.shipping_address.postal_code,
                country: order.shipping_address.country,
              },
            },
          },
        }
      );

      if (stripeError) {
        // Display the error message from Stripe
        let errorMessage = stripeError.message;
        
        // Add more context for specific error types
        if (stripeError.code === 'processing_error') {
          errorMessage = 'An error occurred while processing your card. Please try again in a few moments or use a different payment method.';
        } else if (stripeError.code === 'card_declined') {
          errorMessage = 'Your card was declined. Please check your card details or try a different card.';
        } else if (stripeError.decline_code) {
          errorMessage += ` (Decline code: ${stripeError.decline_code})`;
        }
        
        setError(errorMessage);
        setProcessing(false);
        return;
      }

      if (paymentIntent.status === 'succeeded') {
        // Payment succeeded! Now confirm with backend to update order status
        try {
          await api.payments.confirmPayment(orderId);
        } catch (confirmError) {
          console.error('Error confirming payment:', confirmError);
          // Payment went through but confirmation failed - still show success
          // The webhook should catch it eventually
        }
        
        setSuccess(true);
        
        // Redirect to success page after 2 seconds
        setTimeout(() => {
          navigate(`/payment-success?order_id=${orderId}`);
        }, 2000);
      } else if (paymentIntent.status === 'requires_payment_method') {
        // Payment failed and requires a new payment method
        setError('Payment failed. Please check your card details and try again, or use a different payment method.');
        setProcessing(false);
      } else if (paymentIntent.status === 'processing') {
        // Payment is still processing
        setError('Your payment is being processed. Please wait a moment and refresh the page to check the status.');
        setProcessing(false);
      } else {
        setError(`Payment status: ${paymentIntent.status}. Please contact support if you need assistance.`);
        setProcessing(false);
      }
    } catch (err) {
      console.error('Payment error:', err);
      
      // Handle different types of errors
      let errorMessage = 'Payment failed. Please try again.';
      
      if (err.response?.data?.detail) {
        const detail = err.response.data.detail;
        
        // Check if it's a stock availability error
        if (detail.includes('no longer available') || detail.includes('Insufficient stock') || detail.includes('Failed to reserve stock')) {
          errorMessage = 'Insufficient Stock\n\n' +
            'Unfortunately, one or more items in your order are currently out of stock. ' +
            'This can happen when multiple customers are purchasing at the same time.\n\n' +
            'Please check back later or contact us via email or phone if the items remain unavailable. ' +
            'We apologize for any inconvenience.';
        } else {
          errorMessage = detail;
        }
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      setError(errorMessage);
      setProcessing(false);
    }
  };

  // Styles
  const containerStyles = {
    maxWidth: '600px',
    margin: '0 auto',
    padding: `${theme.spacing.xxl} ${theme.spacing.lg}`,
    minHeight: '70vh',
  };

  const inputStyles = {
    width: '100%',
    padding: theme.spacing.md,
    border: `2px solid ${theme.colors.border}`,
    borderRadius: theme.borderRadius.lg,
    fontSize: theme.typography.fontSize.base,
    fontFamily: theme.typography.fontFamily.secondary,
    transition: 'all 0.3s ease',
    backgroundColor: theme.colors.surface,
  };

  const labelStyles = {
    display: 'block',
    marginBottom: theme.spacing.sm,
    fontSize: theme.typography.fontSize.base,
    fontWeight: theme.typography.fontWeight.semibold,
    color: theme.colors.text,
    fontFamily: theme.typography.fontFamily.secondary,
  };

  // Loading state
  if (loading) {
    return (
      <div style={{ 
        ...containerStyles, 
        textAlign: 'center',
        backgroundColor: theme.colors.background,
      }}>
        <Card 
          padding="xxl"
          style={{
            background: `linear-gradient(to bottom, ${theme.colors.surface} 0%, ${theme.colors.surfaceAlt} 100%)`,
            border: `1px solid ${theme.colors.borderLight}`,
            boxShadow: theme.shadows.xl,
          }}
        >
          <div style={{
            fontSize: '3rem',
            marginBottom: theme.spacing.lg,
            animation: 'pulse 1.5s ease-in-out infinite',
          }}>
            ⏳
          </div>
          <p style={{ 
            fontSize: theme.typography.fontSize.xl,
            color: theme.colors.text,
            fontWeight: theme.typography.fontWeight.semibold,
          }}>
            Loading payment details...
          </p>
        </Card>
        <style>{`
          @keyframes pulse {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.5; }
          }
        `}</style>
      </div>
    );
  }

  // Error state
  if (error && !order) {
    return (
      <div style={{ 
        ...containerStyles, 
        textAlign: 'center',
        backgroundColor: theme.colors.background,
      }}>
        <Card 
          padding="xxl"
          style={{
            background: `linear-gradient(to bottom, ${theme.colors.surface} 0%, ${theme.colors.surfaceAlt} 100%)`,
            border: `1px solid ${theme.colors.borderLight}`,
            boxShadow: theme.shadows.xl,
          }}
        >
          <div style={{ 
            fontSize: '4rem', 
            marginBottom: theme.spacing.xl, 
            color: theme.colors.error 
          }}>
            ✕
          </div>
          <h2 style={{
            fontFamily: theme.typography.fontFamily.primary,
            fontSize: theme.typography.fontSize['3xl'],
            color: theme.colors.error,
            marginBottom: theme.spacing.lg,
            fontWeight: theme.typography.fontWeight.bold,
          }}>
            Payment Error
          </h2>
          <p style={{ 
            marginBottom: theme.spacing.xxl, 
            color: theme.colors.textSecondary,
            fontSize: theme.typography.fontSize.lg,
            lineHeight: theme.typography.lineHeight.relaxed,
          }}>
            {error}
          </p>
          <button
            onClick={() => navigate('/')}
            style={{
              padding: `${theme.spacing.md} ${theme.spacing.xxl}`,
              fontSize: theme.typography.fontSize.lg,
              fontWeight: theme.typography.fontWeight.bold,
              fontFamily: theme.typography.fontFamily.secondary,
              background: `linear-gradient(135deg, ${theme.colors.cta} 0%, ${theme.colors.primaryLight} 100%)`,
              color: '#fff',
              border: 'none',
              borderRadius: theme.borderRadius.lg,
              boxShadow: '0 4px 12px rgba(204, 85, 0, 0.3)',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-3px)';
              e.currentTarget.style.boxShadow = '0 8px 20px rgba(204, 85, 0, 0.4)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(204, 85, 0, 0.3)';
            }}
          >
            Go to Home
          </button>
        </Card>
      </div>
    );
  }

  // Success state
  if (success) {
    return (
      <div style={{ 
        ...containerStyles, 
        textAlign: 'center',
        backgroundColor: theme.colors.background,
      }}>
        <Card 
          padding="xxl"
          style={{
            background: `linear-gradient(to bottom, ${theme.colors.surface} 0%, ${theme.colors.surfaceAlt} 100%)`,
            border: `1px solid ${theme.colors.borderLight}`,
            boxShadow: theme.shadows.xl,
          }}
        >
          <div style={{ 
            fontSize: '6rem', 
            marginBottom: theme.spacing.xl, 
            color: theme.colors.success,
            animation: 'scaleIn 0.5s ease-out',
          }}>
            ✓
          </div>
          <h2 style={{
            fontFamily: theme.typography.fontFamily.primary,
            fontSize: theme.typography.fontSize['4xl'],
            color: theme.colors.success,
            marginBottom: theme.spacing.lg,
            fontWeight: theme.typography.fontWeight.bold,
          }}>
            Payment Successful!
          </h2>
          <p style={{ 
            marginBottom: theme.spacing.md,
            fontSize: theme.typography.fontSize.xl,
            color: theme.colors.text,
            fontWeight: theme.typography.fontWeight.semibold,
          }}>
            Your payment has been processed successfully.
          </p>
          <p style={{ 
            fontSize: theme.typography.fontSize.base, 
            color: theme.colors.textSecondary,
            animation: 'pulse 1.5s ease-in-out infinite',
          }}>
            Redirecting...
          </p>
        </Card>
        <style>{`
          @keyframes scaleIn {
            from {
              transform: scale(0);
              opacity: 0;
            }
            to {
              transform: scale(1);
              opacity: 1;
            }
          }
          @keyframes pulse {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.5; }
          }
        `}</style>
      </div>
    );
  }

  // Payment form
  return (
    <div style={{ backgroundColor: theme.colors.background, minHeight: '100vh' }}>
      <div style={containerStyles}>
        <SectionTitle size="xl" withDivider>
          Complete Payment
        </SectionTitle>

        {stockReserved && !error && (
          <Card 
            padding="lg" 
            style={{ 
              background: `linear-gradient(135deg, ${theme.colors.success} 0%, #16a34a 100%)`,
              color: '#fff',
              marginBottom: theme.spacing.xl,
              border: 'none',
              boxShadow: '0 4px 12px rgba(34, 197, 94, 0.3)',
            }}
          >
            <div style={{ 
              display: 'flex', 
              alignItems: 'start',
              gap: theme.spacing.md,
            }}>
              <span style={{ fontSize: '1.5rem', flexShrink: 0 }}>✓</span>
              <div>
                <strong style={{ 
                  display: 'block', 
                  marginBottom: theme.spacing.sm,
                  fontSize: theme.typography.fontSize.lg,
                  fontWeight: theme.typography.fontWeight.bold,
                }}>
                  Items Reserved
                </strong>
                <div style={{ 
                  fontSize: theme.typography.fontSize.base,
                  lineHeight: theme.typography.lineHeight.relaxed,
                }}>
                  Your items have been reserved and are waiting for payment completion.
                </div>
              </div>
            </div>
          </Card>
        )}

        {error && (
          <Card 
            padding="lg" 
            style={{ 
              background: `linear-gradient(135deg, ${theme.colors.error} 0%, #dc2626 100%)`,
              color: '#fff',
              marginBottom: theme.spacing.xl,
              border: 'none',
              boxShadow: '0 4px 12px rgba(239, 68, 68, 0.3)',
            }}
          >
            <div style={{ 
              display: 'flex', 
              alignItems: 'start',
              gap: theme.spacing.md,
            }}>
              <span style={{ fontSize: '1.5rem', flexShrink: 0 }}>
                {error.includes('Insufficient Stock') ? '📦' : '⚠️'}
              </span>
              <div>
                <strong style={{ 
                  display: 'block', 
                  marginBottom: theme.spacing.sm,
                  fontSize: theme.typography.fontSize.lg,
                  fontWeight: theme.typography.fontWeight.bold,
                }}>
                  {error.includes('Insufficient Stock') ? 'Item Unavailable' : 'Payment Error'}
                </strong>
                <div style={{ 
                  fontSize: theme.typography.fontSize.base,
                  lineHeight: theme.typography.lineHeight.relaxed,
                  whiteSpace: 'pre-line',
                }}>
                  {error}
                </div>
              </div>
            </div>
          </Card>
        )}

        <Card 
          padding="xl" 
          style={{ 
            marginBottom: theme.spacing.xl,
            background: `linear-gradient(to bottom, ${theme.colors.surface} 0%, ${theme.colors.surfaceAlt} 100%)`,
            border: `1px solid ${theme.colors.borderLight}`,
            boxShadow: theme.shadows.lg,
          }}
        >
          <h3 style={{
            fontFamily: theme.typography.fontFamily.primary,
            fontSize: theme.typography.fontSize['2xl'],
            fontWeight: theme.typography.fontWeight.bold,
            marginBottom: theme.spacing.xl,
            paddingBottom: theme.spacing.md,
            borderBottom: `2px solid ${theme.colors.primary}`,
            color: theme.colors.text,
          }}>
            Order Summary
          </h3>

          <div style={{ marginBottom: theme.spacing.lg }}>
            <p style={{ 
              fontSize: theme.typography.fontSize.sm, 
              color: theme.colors.textSecondary,
              marginBottom: theme.spacing.xs,
            }}>
              Order ID: {order?.id?.substring(0, 8)}...
            </p>
            <p style={{ 
              fontSize: theme.typography.fontSize.sm, 
              color: theme.colors.textSecondary,
              marginBottom: theme.spacing.md,
            }}>
              Status: {order?.status}
            </p>

            {/* Order Items */}
            <div style={{ 
              borderTop: `1px solid ${theme.colors.border}`,
              borderBottom: `1px solid ${theme.colors.border}`,
              padding: `${theme.spacing.md} 0`,
              marginBottom: theme.spacing.md,
            }}>
              {order?.items?.map((item, idx) => (
                <div 
                  key={idx}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    marginBottom: theme.spacing.sm,
                    fontSize: theme.typography.fontSize.sm,
                  }}
                >
                  <span>
                    {item.name} × {item.qty}
                  </span>
                  <span>${(Number(item.subtotal) || 0).toFixed(2)}</span>
                </div>
              ))}
            </div>

            {/* Totals */}
            <div style={{ marginBottom: theme.spacing.sm }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: theme.spacing.xs }}>
                <span>Subtotal</span>
                <span>${(Number(order?.subtotal) || 0).toFixed(2)}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: theme.spacing.xs }}>
                <span>Shipping</span>
                <span>${(Number(order?.shipping_cost) || 0).toFixed(2)}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: theme.spacing.md }}>
                <span>Tax</span>
                <span>${(Number(order?.tax) || 0).toFixed(2)}</span>
              </div>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                fontSize: theme.typography.fontSize.xl,
                fontWeight: theme.typography.fontWeight.bold,
                borderTop: `2px solid ${theme.colors.border}`,
                paddingTop: theme.spacing.md,
              }}>
                <span>Total</span>
                <span style={{ color: theme.colors.primary }}>
                  ${(Number(order?.total) || 0).toFixed(2)}
                </span>
              </div>
            </div>
          </div>

          {/* Shipping Address */}
          <div style={{ 
            backgroundColor: theme.colors.backgroundAlt,
            padding: theme.spacing.md,
            borderRadius: theme.borderRadius.md,
            fontSize: theme.typography.fontSize.sm,
          }}>
            <h4 style={{ marginBottom: theme.spacing.xs, fontWeight: theme.typography.fontWeight.semibold }}>
              Shipping Address
            </h4>
            <p style={{ margin: 0, color: theme.colors.textSecondary }}>
              {order?.shipping_address?.full_name}<br />
              {order?.shipping_address?.address_line1}<br />
              {order?.shipping_address?.address_line2 && <>{order.shipping_address.address_line2}<br /></>}
              {order?.shipping_address?.city}, {order?.shipping_address?.state} {order?.shipping_address?.postal_code}<br />
              {order?.shipping_address?.country}
            </p>
          </div>
        </Card>

        <form onSubmit={handlePayment}>
          <Card 
            padding="xl" 
            style={{ 
              marginBottom: theme.spacing.xl,
              background: `linear-gradient(to bottom, ${theme.colors.surface} 0%, ${theme.colors.surfaceAlt} 100%)`,
              border: `1px solid ${theme.colors.borderLight}`,
              boxShadow: theme.shadows.lg,
            }}
          >
            <h3 style={{
              fontFamily: theme.typography.fontFamily.primary,
              fontSize: theme.typography.fontSize['2xl'],
              fontWeight: theme.typography.fontWeight.bold,
              marginBottom: theme.spacing.xl,
              paddingBottom: theme.spacing.md,
              borderBottom: `2px solid ${theme.colors.primary}`,
              color: theme.colors.text,
            }}>
              💳 Payment Information
            </h3>

            <div>
              <label style={labelStyles}>Card Details *</label>
              <div style={{
                padding: theme.spacing.lg,
                border: `2px solid ${theme.colors.border}`,
                borderRadius: theme.borderRadius.lg,
                backgroundColor: theme.colors.surface,
                transition: 'all 0.3s ease',
                boxShadow: theme.shadows.sm,
              }}>
                <CardElement
                  options={{
                    style: {
                      base: {
                        fontSize: '16px',
                        color: theme.colors.text,
                        fontFamily: theme.typography.fontFamily.secondary,
                        '::placeholder': {
                          color: theme.colors.textSecondary,
                        },
                      },
                      invalid: {
                        color: theme.colors.error,
                      },
                    },
                  }}
                />
              </div>
            </div>
          </Card>

          <button
            type="submit"
            disabled={!stripe || processing}
            style={{
              width: '100%',
              padding: `${theme.spacing.md} ${theme.spacing.xl}`,
              fontSize: theme.typography.fontSize.lg,
              fontWeight: theme.typography.fontWeight.bold,
              fontFamily: theme.typography.fontFamily.secondary,
              background: !stripe || processing
                ? theme.colors.borderLight
                : `linear-gradient(135deg, ${theme.colors.cta} 0%, ${theme.colors.primaryLight} 100%)`,
              color: '#fff',
              border: 'none',
              borderRadius: theme.borderRadius.lg,
              boxShadow: !stripe || processing
                ? 'none'
                : '0 4px 12px rgba(204, 85, 0, 0.3)',
              cursor: !stripe || processing ? 'not-allowed' : 'pointer',
              transition: 'all 0.3s ease',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: theme.spacing.sm,
              opacity: !stripe || processing ? 0.6 : 1,
            }}
            onMouseEnter={(e) => {
              if (stripe && !processing) {
                e.currentTarget.style.transform = 'translateY(-3px)';
                e.currentTarget.style.boxShadow = '0 8px 20px rgba(204, 85, 0, 0.4)';
                const icon = e.currentTarget.querySelector('.pay-icon');
                if (icon) icon.style.transform = 'scale(1.1)';
              }
            }}
            onMouseLeave={(e) => {
              if (stripe && !processing) {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(204, 85, 0, 0.3)';
                const icon = e.currentTarget.querySelector('.pay-icon');
                if (icon) icon.style.transform = 'scale(1)';
              }
            }}
          >
            {processing ? (
              <>
                <span style={{
                  display: 'inline-block',
                  width: '20px',
                  height: '20px',
                  border: '3px solid rgba(255,255,255,0.3)',
                  borderTop: '3px solid #fff',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite',
                }} />
                <span>Processing Payment...</span>
              </>
            ) : (
              <>
                <span 
                  className="pay-icon"
                  style={{
                    fontSize: '1.3rem',
                    transition: 'transform 0.3s ease',
                    display: 'inline-block',
                  }}
                >
                  💳
                </span>
                <span>Pay ${(Number(order?.total) || 0).toFixed(2)}</span>
              </>
            )}
          </button>

          <p style={{
            marginTop: theme.spacing.lg,
            fontSize: theme.typography.fontSize.sm,
            color: theme.colors.textSecondary,
            textAlign: 'center',
            lineHeight: theme.typography.lineHeight.relaxed,
          }}>
            🔒 Your payment is secure and encrypted. By proceeding, you agree to complete this purchase.
          </p>
          
          <style>{`
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          `}</style>
        </form>
      </div>
    </div>
  );
};

export default PaymentPage;

